<template>
  <div v-if="storyboard">
    <div class="row">
      <!-- begin storyboard card -->
      <div class="col-xl-12 m-b-20">
        <b-card
          no-body
          class="overflow-hidden"
          bg-variant="dark"
          text-variant="light"
        >
          <b-row no-gutters>
            <b-col md="2">
              <b-card-img
                :src="getImage(storyboard.image)"
                alt="Image"
                class="rounded-0"
              ></b-card-img>
            </b-col>
            <b-col md="10" class="p-0">
              <b-card-body :title="storyboard.name">
                <b-card-text>
                  {{ storyboard.description }}
                </b-card-text>
              </b-card-body>
            </b-col>
          </b-row>
        </b-card>
      </div>
      <!-- end storyboard card -->
    </div>
    <div class="row">
      <div class="col-xl-12 m-b-20">
        <b-card no-body>
          <b-tabs
            justified
            nav-class="nav-tabs-inverse"
            nav-wrapper-class="nav-tabs-inverse"
            v-model="tabIndex"
          >
            <div class="p-20">
              <!-- begin stages tab -->
              <b-tab
                title="Set stages to storyboard"
                active
                @click="setTabIndex(0)"
              >
                <storyboard-filter
                  :items="filteredStages"
                  :getImage="getImage"
                  :itemViewUrl="`/storyboard/${$route.params.id}/stage/`"
                  :addButton="{
                    title: 'Add stage',
                    label: 'Add new stage',
                    url: `/storyboard/${$route.params.id}/stage/new`
                  }"
                  :removeFromParent="removeStage"
                  :colsLength="3"
                  :perPage="11"
                >
                  <template slot="extraButtons">
                    <ordering-tool
                      model="stage"
                      :refresh-action="getStages"
                      :update-action="updateStageAction"
                      :data="filteredStages"
                      button-text="Set order for Stages"
                      :option-label="'name'"
                    />
                  </template>
                </storyboard-filter>
              </b-tab>
              <!-- end stages tab -->
              <!-- begin steps tab -->
              <b-tab
                title="Set steps to stage"
                @click="setTabIndex(1)"
                :disabled="!filteredStages.length"
              >
                <div class="row" v-if="activeStage">
                  <div class="col-3">
                    <b-card
                      bg-variant="dark"
                      footer-bg-variant="dark"
                      text-variant="light"
                      :img-src="getImage(activeStage.image)"
                      img-alt="Image"
                      img-top
                    >
                      <b-row>
                        <b-col class="text-center">
                          <h4>{{ activeStage.name }}</h4>
                          Storyboard stage
                          {{
                            filteredStages.length > 1
                              ? `(${index + 1}/${filteredStages.length})`
                              : ''
                          }}
                        </b-col>
                      </b-row>
                      <b-row>
                        <b-col class="d-flex">
                          <button
                            v-if="true"
                            @click="previousStage"
                            class="
                              btn btn-yellow btn-icon btn-circle btn-lg
                              my-auto
                            "
                          >
                            <i class="fa fa-chevron-left" />
                          </button>
                        </b-col>
                        <b-col class="d-flex">
                          <button
                            v-if="true"
                            @click="nextStage"
                            class="
                              btn btn-yellow btn-icon btn-circle btn-lg
                              pull-right
                              my-auto
                              ml-auto
                            "
                          >
                            <i class="fa fa-chevron-right" />
                          </button>
                        </b-col>
                      </b-row>
                    </b-card>
                  </div>
                  <div class="col-9">
                    <storyboard-filter
                      :items="filterSteps($route.params.id)"
                      :stages="storyboard.stages"
                      :getImage="getImage"
                      :activeStage="activeStage"
                      :itemViewUrl="`/storyboard/${$route.params.id}/stage/`"
                      :addButton="{
                        title: 'Add step',
                        label: 'Add new step',
                        url: `/storyboard/${$route.params.id}/stage/${
                          activeStage ? activeStage.id : null
                        }/step/new`
                      }"
                      :removeFromParent="removeStep"
                      :colsLength="4"
                      :perPage="8"
                    >
                      <template slot="extraButtons">
                        <ordering-tool
                          model="step"
                          :refresh-action="getSteps"
                          :update-action="updateStepAction"
                          :data="activeStageSteps"
                          button-text="Set order for Steps"
                          :option-label="'name'"
                        />
                      </template>
                    </storyboard-filter>
                  </div>
                </div>
                <div class="row" v-else>
                  <div class="col-3 border rounded text-center p-10 bg-light">
                    No stages set
                  </div>
                </div>
              </b-tab>
              <!-- end steps tab -->
            </div>
          </b-tabs>
        </b-card>
        <!-- end panel -->
      </div>
    </div>
    <div class="row">
      <div class="col-xl-12">
        <!-- begin orphan stages panel -->
        <panel
          v-if="tabIndex == 0 && filterOprhanStages().length"
          no-button="true"
          title="Add stages to storyboard"
        >
          <storyboard-filter
            :items="filterOprhanStages()"
            :getImage="getImage"
            :addToParent="addStage"
            :showStageFilter="false"
            :colsLength="3"
            :perPage="12"
          />
        </panel>
        <!-- end orphan stages panel -->
        <!-- begin orphan steps panel -->
        <panel
          v-else-if="tabIndex == 1 && filterOprhanSteps().length"
          no-button="true"
          title="Add steps to active stage"
        >
          <storyboard-filter
            :items="filterOprhanSteps()"
            :getImage="getImage"
            :addToParent="addStep"
            :activeStage="activeStage"
            :colsLength="3"
            :perPage="12"
          />
        </panel>
        <!-- end orphan steps panel -->
      </div>
    </div>
    <b-card
      class="m-b-20"
      header-tag="header"
      header-bg-variant="dark"
      header-text-variant="light"
    >
      <template v-slot:header>
        <h6 class="mb-0">Actions</h6>
      </template>
      <url-button class="btn-secondary" label="Back" :url="'/storyboard'" />
      <url-button
        class="btn-yellow pull-right"
        label="View"
        :url="`/storyboard/${storyboard.id}`"
      />
    </b-card>
  </div>
</template>

<script>
import UrlButton from '../../components/buttons/UrlButton.vue'
import StoryboardFilter from '../storyboard/StoryboardFilter.vue'
import OrderingTool from '../shared/OrderingTool.vue'
import { mapActions } from 'vuex'

export default {
  name: 'StoryboardPlan',
  props: ['storyboard', 'stages', 'steps', 'scriptItems'],
  components: {
    UrlButton,
    StoryboardFilter,
    OrderingTool
  },
  data() {
    return {
      tabIndex: 0,
      sItems: [],
      filteredStages: [],
      index: 0,
      activeStage: null
    }
  },
  computed: {
    activeStageSteps() {
      return this.filterSteps(this.$route.params.id).filter(
        (step) => step.stage === this.activeStage.id
      )
    }
  },
  methods: {
    ...mapActions('stage', ['getStages', 'updateStage']),
    ...mapActions('step', ['getSteps', 'updateStep']),
    updateStageAction(data) {
      this.updateStage(data)
      const eventId = this.$store.state.event.activeEvent.id
      this.getStages(eventId)
      this.getSteps(eventId)
    },
    updateStepAction(data) {
      this.updateStep(data)
      const eventId = this.$store.state.event.activeEvent.id
      this.getStages(eventId)
      this.getSteps(eventId)
    },
    setTabIndex(index) {
      this.tabIndex = index
    },
    filterStages(storyboardId) {
      this.filteredStages = this.stages
        .filter(function (stage) {
          if (stage.storyboard == storyboardId) {
            return stage
          }
        })
        .sort(function (a, b) {
          return a.order - b.order
        })
      this.activeStage = this.filteredStages[this.index]
    },
    filterOprhanStages() {
      return this.stages.filter(function (stage) {
        return stage.storyboard == null
      })
    },
    filterSteps(sbId) {
      let steps = []
      this.stages.forEach(function (stage) {
        if (stage.storyboard == sbId) {
          stage.steps.forEach((step) => {
            steps.push(step)
          })
        }
      })
      steps = steps.sort(function (a, b) {
        return a.order - b.order
      })
      return steps
    },
    filterOprhanSteps() {
      return this.steps.filter(function (step) {
        return step.stage == null
      })
    },
    addStage(stage) {
      if (confirm(`Add ${stage.name} to ${this.storyboard.name}?`)) {
        this.$parent.$parent.stageEditStoryboard(
          stage.id,
          JSON.stringify({
            storyboard: this.storyboard.id
          })
        )
        //this.$router.go();
      }
    },
    removeStage(stage) {
      if (confirm(`Remove ${stage.name} from ${this.storyboard.name}?`)) {
        this.$parent.$parent.stageEditStoryboard(
          stage.id,
          JSON.stringify({
            storyboard: null
          })
        )
        //this.$router.go();
      }
    },
    addStep(step) {
      if (confirm(`Add ${step.name} to ${this.activeStage.name}?`)) {
        this.$parent.$parent.stepEditStage(
          step.id,
          JSON.stringify({
            stage: this.activeStage.id
          })
        )
        //this.$router.go();
      }
    },
    removeStep(step) {
      if (confirm(`Remove ${step.name} from ${this.activeStage.name}?`)) {
        this.$parent.$parent.stepEditStage(
          step.id,
          JSON.stringify({
            stage: null
          })
        )
        //this.$router.go();
      }
    },
    getImage(image) {
      if (image == null) {
        return require('../../assets/images/no_image.jpg')
      } else {
        return process.env.VUE_APP_API_URL + '/uploads/images/' + image.imageFd
      }
    },
    getTypeName(typeId) {
      switch (typeId) {
        case 1:
          return 'service'
        case 2:
          return 'space'
        case 3:
          return 'staff'
        case 4:
          return 'stuff'
        default:
          return ''
      }
    },
    previousStage() {
      if (this.index == 0) {
        this.index = this.filteredStages.length - 1
      } else {
        this.index--
      }
      this.showStageById(this.filteredStages[this.index].id)
    },
    nextStage() {
      if (this.index == this.filteredStages.length - 1) {
        this.index = 0
      } else {
        this.index++
      }
      this.showStageById(this.filteredStages[this.index].id)
    },
    showStageById(stageId) {
      this.filteredStages.forEach((fStage) => {
        if (fStage.id == stageId) {
          this.activeStage = fStage
        }
      })
    }
  },
  mounted() {
    this.filterStages(this.$route.params.id)
    this.activeStage = this.filteredStages[0]
  },
  watch: {
    stages: function () {
      this.filterStages(this.$route.params.id)
    }
  }
}
</script>
